import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { MaskedLine, Eyebrow, easeOut } from "@/components/Reveal";
import { CONTACT, APPS } from "@/lib/content";
import { useLocation, useNavigate } from "react-router-dom";

const HERO_IMG = "/newmarket-hero.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goContact = () => {
    if (pathname !== "/") return navigate("/", { state: { anchor: "contact" } });
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#FFFFFF] pt-[72px]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute -left-40 top-1/4 h-[540px] w-[540px] rounded-full bg-[#3682AE]/[0.07] blur-3xl" aria-hidden="true" />
      <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto grid w-full max-w-[1320px] grid-cols-1 items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <Eyebrow>Ford &amp; Associates — Newmarket, Ontario</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-display text-[13.5vw] font-medium leading-[0.98] tracking-tight text-[#102036] sm:text-6xl lg:text-[76px]">
            <MaskedLine delay={0.25}>Accounting. Tax.</MaskedLine>
            <MaskedLine delay={0.4}>Advisory.</MaskedLine>
            <MaskedLine delay={0.62} className="mt-2">
              <em className="text-[#102036]/90">Confidence behind</em>
            </MaskedLine>
            <MaskedLine delay={0.74}>
              <em className="text-[#102036]/90">every number.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: easeOut }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#4A5568] sm:text-lg"
          >
            Professional accounting, taxation, CRA representation and business advisory services for individuals
            and businesses — delivered personally, supported by secure technology.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease: easeOut }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={goContact}
              data-testid="hero-get-started-button"
              className="group inline-flex items-center gap-3 rounded-full bg-[#102036] px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_12px_32px_rgb(16,32,54,0.30)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#102036]"
            >
              Get started
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => navigate("/client-centre")}
              data-testid="hero-client-centre-button"
              className="inline-flex items-center gap-3 rounded-full border border-[#102036]/20 bg-white/60 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-[#102036] backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-[#102036] hover:bg-white"
            >
              Client Centre
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[#4A5568]"
          >
            <span className="font-semibold uppercase tracking-[0.16em] text-[#102036]/50 text-[11px]">Existing clients</span>
            <a href={APPS.clientManager.url} target="_blank" rel="noopener noreferrer" data-testid="hero-client-manager-link" className="link-sweep inline-flex items-center gap-1.5 font-semibold text-[#102036]">
              {APPS.clientManager.name} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <a href={APPS.taxManager.url} target="_blank" rel="noopener noreferrer" data-testid="hero-tax-manager-link" className="link-sweep inline-flex items-center gap-1.5 font-semibold text-[#102036]">
              {APPS.taxManager.name} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.55, ease: easeOut }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div className="absolute -inset-3 rounded-lg border border-[#102036]/15" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-lg shadow-[0_30px_70px_rgb(12,24,42,0.18)]">
              <img
                src={HERO_IMG}
                alt="Aerial view of Main Street South and Old Town Hall, Newmarket, Ontario — home of Ford & Associates"
                className="h-[420px] w-full object-cover lg:h-[560px]"
                loading="eager"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#102036]/40 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[#FFFFFF]">
                <span className="text-[11px] font-bold uppercase tracking-[0.24em]">Serving clients across Ontario</span>
                <span className="font-display text-lg italic">Main Street, Newmarket</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#quick-access"
        onClick={(e) => { e.preventDefault(); document.getElementById("quick-access")?.scrollIntoView({ behavior: "smooth" }); }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#102036]/50 transition-colors hover:text-[#102036] md:flex"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to client quick access"
      >
        Scroll
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.a>
    </section>
  );
}
